import React from 'react';
import { useTranslation } from 'react-i18next';
import type {
    ActionArea,
    ControlAction,
    PaletteResolvers,
} from '../../controlCenterMeta';
import { CommandPaletteView } from './CommandPaletteView';
import { useCommandPalette } from './useCommandPalette';

type CommandPaletteProps = {
    isOpen: boolean;
    onClose: () => void;
    onRun: (action: ControlAction) => void;
    onTogglePin: (id: string) => void;
    /** Whether this session is refused an action — muted, not runnable. */
    isRefused: (id: string) => boolean;
    totalCount: number;
};

/**
 * Self-served: the palette owns its query, its highlight and its keyboard, and
 * is handed only what belongs to the page — whether it is open, and what to do
 * with the row that gets chosen.
 *
 * The catalog is a fixture, so there is nothing to fetch here and nothing to
 * pass down. What this container does own is the label resolution: searching
 * matches what the reader can see, which in this app is one of two languages,
 * so the names come from `t` at the moment the query runs rather than from the
 * fixture.
 *
 * `isRefused` rides along with the label resolvers for the same reason: a row
 * this session cannot run has to be findable and unrunnable, and the palette
 * has nowhere to anchor the refusal popover the catalog rows use.
 */
export const CommandPalette: React.FC<CommandPaletteProps> = ({
    isOpen,
    onClose,
    onRun,
    onTogglePin,
    isRefused,
    totalCount,
}) => {
    const { t } = useTranslation('pages');

    const resolvers = React.useMemo<PaletteResolvers>(
        () => ({
            labelOf: (id: string) => t(`controlCenter.actions.${id}`),
            areaLabelOf: (area: ActionArea) => t(`controlCenter.areas.${area}`),
            isRefused,
        }),
        [isRefused, t],
    );

    const palette = useCommandPalette({
        isOpen,
        resolvers,
        onRun,
        onTogglePin,
        onClose,
    });

    return (
        <CommandPaletteView
            highlight={palette.highlight}
            highlightedId={palette.highlightedId}
            isOpen={isOpen}
            onClose={onClose}
            onHighlight={palette.onHighlight}
            onKeyDown={palette.onKeyDown}
            onQueryChange={palette.onQueryChange}
            onRun={onRun}
            query={palette.query}
            results={palette.results}
            selectable={palette.selectable}
            totalCount={totalCount}
        />
    );
};
